import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useUploads } from "@/context/UploadContext";
import { UploadCloud, CheckCircle2, AlertCircle, Loader2, RotateCcw, X, Sparkles } from "lucide-react";
import { retryText } from "@/components/UploadIssuesBanner";

const STATUS_LABEL = {
  queued: "Wartend",
  uploading: "Lädt hoch",
  processing: "KI verarbeitet",
  retrying: "Wartet auf neuen Versuch",
  done: "Fertig",
  error: "Fehler",
};

function fmtSize(b) {
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
}

function StatusIcon({ status }) {
  if (status === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "error") return <AlertCircle className="h-4 w-4 text-rose-600" />;
  if (status === "processing") return <Sparkles className="h-4 w-4 text-indigo-600 animate-pulse" />;
  if (status === "retrying") return <RotateCcw className="h-4 w-4 text-amber-600 animate-spin [animation-duration:3s]" />;
  if (status === "uploading") return <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />;
  return <UploadCloud className="h-4 w-4 text-slate-400" />;
}

function UploadRow({ u, onRetry, onDismiss }) {
  const busy = ["uploading", "processing"].includes(u.status);
  const sub = u.status === "error" || u.status === "retrying" ? retryText(u) : `${STATUS_LABEL[u.status]} · ${fmtSize(u.size)}`;
  return (
    <li className="py-2 border-b border-slate-100 last:border-0" data-testid={`upload-row-${u.id}`}>
      <div className="flex items-center gap-2">
        <StatusIcon status={u.status} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-900 truncate" title={u.name}>{u.name}</p>
          <p className={`text-xs ${u.status === "error" ? "text-rose-600" : u.status === "retrying" ? "text-amber-700" : "text-slate-500"}`} title={sub} data-testid={`upload-status-${u.id}`}>
            {sub}
          </p>
        </div>
        {(u.status === "error" || u.status === "retrying") && (
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onRetry(u.id)} title={u.status === "retrying" ? "Jetzt erneut versuchen" : "Erneut versuchen"} data-testid={`upload-retry-${u.id}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
        {!busy && (
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onDismiss(u.id)} title="Entfernen" data-testid={`upload-dismiss-${u.id}`}>
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {u.status !== "error" && u.status !== "retrying" && (
        <div className="mt-1.5 h-1 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-300 ${u.status === "done" ? "bg-emerald-500" : u.status === "processing" ? "bg-indigo-400 animate-pulse" : "bg-indigo-600"}`}
            style={{ width: `${u.progress}%` }}
          />
        </div>
      )}
    </li>
  );
}

export function UploadProgressBar() {
  const { uploads, active, retry, retryAllFailed, dismiss, clearFinished } = useUploads();
  if (!uploads.length) return null;

  const total = uploads.length;
  const doneCount = uploads.filter((u) => u.status === "done").length;
  const errCount = uploads.filter((u) => u.status === "error").length;
  const retryCount = uploads.filter((u) => u.status === "retrying").length;
  const overall = Math.round(uploads.reduce((s, u) => s + (u.status === "done" ? 100 : u.progress), 0) / total);
  const allFinished = active.length === 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative flex items-center gap-2 h-8 px-3 rounded-full border text-xs transition-colors overflow-hidden ${errCount ? "border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100" : retryCount ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"}`}
          data-testid="upload-progress-trigger"
        >
          <span className="absolute inset-x-0 bottom-0 h-0.5 bg-slate-100">
            <span
              className={`block h-full transition-[width] duration-300 ${errCount && allFinished ? "bg-rose-500" : allFinished ? "bg-emerald-500" : "bg-indigo-600"}`}
              style={{ width: `${overall}%` }}
            />
          </span>
          {allFinished ? (
            errCount ? <AlertCircle className="h-3.5 w-3.5 text-rose-600" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <Loader2 className="h-3.5 w-3.5 text-indigo-600 animate-spin" />
          )}
          <span className="font-medium tabular-nums" data-testid="upload-progress-summary">
            {allFinished
              ? errCount ? `${errCount} Fehler` : `${doneCount} fertig`
              : retryCount && !uploads.some((u) => ["uploading", "processing"].includes(u.status))
                ? `${retryCount} wartet auf Server`
                : `${doneCount + errCount}/${total} · ${overall}%`}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" data-testid="upload-progress-panel">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold text-slate-900">Uploads</p>
          {(doneCount + errCount) > 0 && (
            <button type="button" className="text-xs text-slate-500 hover:text-slate-900" onClick={clearFinished} data-testid="upload-clear-finished">
              Erledigte ausblenden
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-2">Läuft im Hintergrund weiter — du kannst die Seite wechseln.</p>
        {retryCount > 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="upload-retrying-hint">
            Server vorübergehend nicht erreichbar – {retryCount} Upload(s) werden automatisch wiederholt, bis es klappt. Die Dateien bleiben auch nach Neuladen gespeichert.
          </p>
        )}
        {errCount > 0 && (
          <Button size="sm" variant="outline" className="w-full mb-2 h-8 text-xs border-rose-200 text-rose-700 hover:bg-rose-50" onClick={retryAllFailed} data-testid="upload-retry-all">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Alle {errCount} fehlgeschlagenen erneut versuchen
          </Button>
        )}
        <ul className="max-h-72 overflow-y-auto -mx-1 px-1">
          {uploads.map((u) => <UploadRow key={u.id} u={u} onRetry={retry} onDismiss={dismiss} />)}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
